<script setup lang="ts">
import { useNavQuery } from '~~/app/composables/queries/useStoreApi'

/**
 * Pasek działów katalogu na stronie głównej.
 *
 * Wcześniej były to kafle ze zdjęciami aranżacyjnymi. Klientka poprosiła
 * (16.09.2026) o ikony zamiast fotografii, w firmowym turkusie — wzorem
 * monstera.pl, gdzie pasek działów jest rysunkowy i mieści kilkanaście
 * pozycji w jednym rzędzie. Zdjęcie pokazywało jeden produkt z działu i przez
 * to go zawężało; ikona mówi „to cała półka", a przy okazji strona główna
 * przestaje ciągnąć dziewięć fotografii.
 *
 * Kategorie bierzemy z RZECZYWISTEGO drzewa nawigacji, nie z listy w kodzie:
 * katalog powstaje po stronie EasyItem, więc lista na sztywno wskazywałaby po
 * replikacji nieistniejące działy. Rysunki mapujemy po slugu (stabilny), nie
 * po id (zmieni się przy replikacji) — dział bez rysunku dostaje kształt
 * zapasowy, więc pasek nie ma dziur.
 */
const props = withDefaults(defineProps<{
  title?: string
  /**
   * Ile działów pokazać. Domyślnie wszystkie działy główne — po replikacji
   * jest ich czternaście, a dawny limit 12 chował „Tekstylia" i „Zestawy
   * upominkowe". Bezpiecznik zostaje na wypadek drzewa z kilkudziesięcioma
   * działami głównymi.
   */
  limit?: number
  /** Kafel „wszystkie" na końcu paska — jak we wzorcu. */
  pokazWszystkie?: boolean
}>(), {
  title: 'Kategorie',
  limit: 24,
  pokazWszystkie: true,
})

const { data: nav } = useNavQuery()

const dzialy = computed(() =>
  (nav.value?.items ?? [])
    .filter(item => item.label && item.href)
    .slice(0, props.limit),
)

// Na szerokim ekranie kafle idą w dwa RÓWNE rzędy. `auto-fit` dawał przy
// 1440 px rząd jedenastu i rząd czterech — wyglądało to na urwaną listę.
const kafleWRzedzie = computed(() =>
  Math.max(1, Math.ceil((dzialy.value.length + (props.pokazWszystkie ? 1 : 0)) / 2)),
)
</script>

<template>
  <section
    v-if="dzialy.length"
    class="kategorie-pasek container"
  >
    <h2 class="section-title">
      {{ props.title }}
    </h2>

    <!-- Na wąskim ekranie pasek przewija się poziomo zamiast łamać na siatkę
         o jednej kolumnie — dziewięć pozycji pod sobą zepchnęłoby resztę
         strony poniżej ekranu. -->
    <div
      class="kategorie-pasek__lista"
      :style="{ '--kafle-w-rzedzie': kafleWRzedzie }"
    >
      <NuxtLink
        v-for="dzial in dzialy"
        :key="dzial.href"
        :to="dzial.href"
        class="kategoria"
      >
        <span class="kategoria__pole">
          <KategoriaIkona :nazwa="dzial.slug" class="kategoria__ikona" />
        </span>
        <span class="kategoria__podpis">{{ dzial.label }}</span>
      </NuxtLink>

      <NuxtLink
        v-if="props.pokazWszystkie"
        to="/search"
        class="kategoria kategoria--wszystkie"
      >
        <span class="kategoria__pole">
          <KategoriaIkona nazwa="zapasowa" class="kategoria__ikona" />
        </span>
        <span class="kategoria__podpis">Wszystkie</span>
      </NuxtLink>
    </div>
  </section>
</template>

<style scoped>
.kategorie-pasek {
  margin: 4rem auto;
}

/* SIATKA KAFELKÓW, nie przewijany pasek.

   Pierwsza wersja była paskiem z `overflow-x: auto`. Na telefonie pokazywała
   dwie i pół pozycji, ucinała podpisy w połowie słowa („PODRÓŻ I TOR…")
   i ukrywała siedem działów za przewijaniem, którego nic nie sygnalizowało
   poza cienkim paskiem. Siatka pokazuje komplet od razu.

   Trzy kolumny na telefonie: przy 390 px daje ~118 px na kafelek, czyli
   dość na dwuwierszowy podpis bez dzielenia wyrazów. Dwie kolumny byłyby
   czytelniejsze, ale piętnaście pozycji zajęłoby osiem rzędów i zepchnęło
   produkty poniżej ekranu — przy trzech jest ich pięć, wszystkie pełne.

   Tablet: pięć kolumn, czyli trzy pełne rzędy. Desktop: dwa równe rzędy
   (`--kafle-w-rzedzie` liczone w skrypcie) — przy 15 kaflach osiem
   w rzędzie, ~150 px na kafelek przy 1366 px. */
.kategorie-pasek__lista {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.5rem;
  margin-top: 2rem;
}

@media (min-width: 560px) {
  .kategorie-pasek__lista {
    grid-template-columns: repeat(5, minmax(0, 1fr));
  }
}

@media (min-width: 1100px) {
  .kategorie-pasek__lista {
    grid-template-columns: repeat(var(--kafle-w-rzedzie, 8), minmax(0, 1fr));
  }
}

/* Kafelek: tło niesie kafelek, nie ikona. Wcześniej tłem było kółko pod
   ikoną, a sam kafelek był przezroczysty — przy siatce czytało się to jako
   luźne kropki, nie jak klikalne pola. Teraz całe pole jest celem dotyku,
   co na telefonie ma znaczenie. */
.kategoria {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 0.5rem;
  /* minmax(0,1fr) plus to pozwala kafelkowi zwężać się zamiast rozpychać
     siatkę najdłuższym podpisem. */
  min-width: 0;
  padding: 0.875rem 0.375rem;
  color: inherit;
  text-decoration: none;
  background: color-mix(in srgb, var(--color-accent, #64bfaa) 10%, transparent);
  border-radius: var(--product-img-radius, 8px);
  transition: transform 0.18s ease, background-color 0.18s ease;
}

/* Tło kafelka to rozbielony turkus firmowy. Klientka dopuszczała oba warianty
   („ich tło lub same ikony w turkusie"), więc spoczynek jest lekki, a stan
   aktywny mocny.

   Ikona w dwóch barwach marki z grafik na Instagramie (12.10.2026): kontur
   w ciemnym tealu, detal w turkusie. Sam turkus na rozbielonym turkusie
   miał ~2:1 i rysunki się rozmywały; teal trzyma kontur, a turkus zostaje
   w każdej ikonie jako akcent. */
.kategoria__pole {
  display: grid;
  place-items: center;
  width: 44px;
  height: 44px;
  color: var(--color-brand-deep, var(--color-accent, #64bfaa));
  --kategoria-ikona-akcent: var(--color-accent, #64bfaa);
  --kategoria-ikona-akcent-krycie: 1;
  transition: color 0.18s ease;
}

@media (min-width: 560px) {
  .kategoria__pole {
    width: 52px;
    height: 52px;
  }
}

.kategoria__ikona {
  width: 100%;
  height: 100%;
}

.kategoria__podpis {
  /* Mniejszy stopień i ciaśniejsza spacja niż w pasku: podpis ma się zmieścić
     w dwóch wierszach na 118 px, a „BIURO I PIŚMIENNICZE" to dwadzieścia
     znaków. `overflow-wrap` łamie tylko wtedy, gdy jeden wyraz i tak nie
     wchodzi — bez tego długi wyraz rozpychał kafelek. */
  font-size: 0.625rem;
  font-weight: var(--font-weight-bold, 700);
  letter-spacing: 0.04em;
  line-height: 1.35;
  text-align: center;
  text-transform: uppercase;
  overflow-wrap: anywhere;
  color: var(--color-black, #1f2933);
}

@media (min-width: 560px) {
  .kategoria__podpis {
    font-size: 0.6875rem;
    letter-spacing: 0.06em;
  }
}

@media (hover: hover) {
  .kategoria:hover {
    transform: translateY(-2px);
    background: color-mix(in srgb, var(--color-accent, #64bfaa) 22%, transparent);
  }
}

.kategoria:focus-visible {
  outline: 2px solid var(--color-accent, #64bfaa);
  outline-offset: 3px;
}

/* Kafel „wszystkie" jest nawigacją, nie działem — stąd słabszy głos. */
.kategoria--wszystkie {
  background: color-mix(in srgb, var(--color-muted, #6b7280) 10%, transparent);
}

.kategoria--wszystkie .kategoria__pole {
  color: var(--color-muted, #6b7280);
  --kategoria-ikona-akcent: currentColor;
  --kategoria-ikona-akcent-krycie: 0.55;
}

@media (prefers-reduced-motion: reduce) {
  .kategoria,
  .kategoria__pole {
    transition: none;
  }

  .kategoria:hover {
    transform: none;
  }
}
</style>
